import { FinancialCalculatorService } from "../financialCalculatorService";

/**
 * rev&u pension projection engine — pure calculation functions.
 *
 * Producer forecast fields (projected balances, expected monthly pension, projection return) are NOT inputs here:
 * every future value is computed from current balances with one uniform methodology.
 * All rates are decimals (4% = 0.04). Every method that can lack data returns a CalculationResult,
 * so callers always know which calculations were skipped and why — nothing is filled in by guessing.
 */

export type SkipReason =
  | "MISSING_BIRTH_DATE"
  | "MISSING_TARGET_AGE"
  | "MISSING_CALCULATION_DATE"
  | "TARGET_DATE_NOT_AFTER_CALCULATION_DATE"
  | "INVALID_BALANCE"
  | "INVALID_RETURN"
  | "MISSING_CONTRIBUTION"
  | "MISSING_COEFFICIENT"
  | "AMBIGUOUS_COEFFICIENT";

export type CalculationResult<T> =
  | { status: "calculated"; value: T }
  | { status: "skipped"; reason: SkipReason };

/**
 * How the accumulation fee relates to the annual return.
 * - "gross": the annual return is used as-is (current methodology — the accumulation fee is NOT deducted).
 * - "netOfAccumulationFee": the accumulation fee is deducted from the annual return (reserved for a future methodology).
 */
export type ReturnMethodology = "gross" | "netOfAccumulationFee";

export interface TimeToTarget {
  /** Whole months — the number of monthly contributions (made at the end of each month) */
  months: number;
  /** Exact fractional months, including the remaining days of the last partial month */
  exactMonths: number;
  /** Exact years (exactMonths / 12) — used to grow the existing balance */
  years: number;
}

const isValidNumber = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value);

const parseDate = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export class PensionProjectionEngine {
  static readonly DEFAULT_ANNUAL_RETURN = 0.04;

  // ---------------------------------------------------------------------------
  // Results
  // ---------------------------------------------------------------------------

  static calculated<T>(value: T): CalculationResult<T> {
    return { status: "calculated", value };
  }

  static skipped<T>(reason: SkipReason): CalculationResult<T> {
    return { status: "skipped", reason };
  }

  static isCalculated<T>(result: CalculationResult<T>): result is { status: "calculated"; value: T } {
    return result.status === "calculated";
  }

  /** Value of a result, or null when it was skipped */
  static valueOrNull<T>(result: CalculationResult<T>): T | null {
    return PensionProjectionEngine.isCalculated(result) ? result.value : null;
  }

  // ---------------------------------------------------------------------------
  // Return methodology
  // ---------------------------------------------------------------------------

  static resolveEffectiveAnnualReturn(
    annualReturn: number,
    accumulationFeeRate: number | null | undefined,
    methodology: ReturnMethodology = "gross"
  ): number {
    return methodology === "netOfAccumulationFee" && isValidNumber(accumulationFeeRate)
      ? annualReturn - accumulationFeeRate
      : annualReturn;
  }

  /** Effective monthly rate equivalent to an annual rate: (1 + annual)^(1/12) - 1 */
  static toEffectiveMonthlyRate(annualReturn: number): number {
    return Math.pow(1 + annualReturn, 1 / 12) - 1;
  }

  // ---------------------------------------------------------------------------
  // Time
  // ---------------------------------------------------------------------------

  /** The date the person reaches targetAge (Feb 29 birthdays fall on Feb 28 in non-leap years) */
  static calculateTargetDate(
    birthDate: string | Date | null | undefined,
    targetAge: number | null | undefined
  ): CalculationResult<Date> {
    const birth = parseDate(birthDate);
    if (!birth) return PensionProjectionEngine.skipped("MISSING_BIRTH_DATE");
    if (!isValidNumber(targetAge) || targetAge <= 0) return PensionProjectionEngine.skipped("MISSING_TARGET_AGE");

    const year = birth.getFullYear() + Math.floor(targetAge);
    const month = birth.getMonth();
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
    return PensionProjectionEngine.calculated(new Date(year, month, Math.min(birth.getDate(), lastDayOfMonth)));
  }

  /** Exact time from calculationDate until targetDate */
  static calculateMonthsToTarget(
    calculationDate: string | Date | null | undefined,
    targetDate: Date | null | undefined
  ): CalculationResult<TimeToTarget> {
    const start = parseDate(calculationDate);
    if (!start) return PensionProjectionEngine.skipped("MISSING_CALCULATION_DATE");
    if (!targetDate) return PensionProjectionEngine.skipped("MISSING_TARGET_AGE");
    if (targetDate.getTime() <= start.getTime()) {
      return PensionProjectionEngine.skipped("TARGET_DATE_NOT_AFTER_CALCULATION_DATE");
    }

    let wholeMonths = (targetDate.getFullYear() - start.getFullYear()) * 12 + (targetDate.getMonth() - start.getMonth());
    if (targetDate.getDate() < start.getDate()) wholeMonths -= 1;

    // Remaining days after the last whole month, as a fraction of that month's length
    const anchor = new Date(start.getFullYear(), start.getMonth() + wholeMonths, 1);
    const anchorDay = Math.min(start.getDate(), new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate());
    const anchorDate = new Date(anchor.getFullYear(), anchor.getMonth(), anchorDay);
    const daysInAnchorMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
    const remainingDays = (targetDate.getTime() - anchorDate.getTime()) / (1000 * 60 * 60 * 24);
    const exactMonths = wholeMonths + Math.max(0, remainingDays) / daysInAnchorMonth;

    return PensionProjectionEngine.calculated({ months: wholeMonths, exactMonths, years: exactMonths / 12 });
  }

  // ---------------------------------------------------------------------------
  // Future values
  // ---------------------------------------------------------------------------

  /** FV_existing = currentBalance × (1 + annualReturn) ^ yearsToTarget — no further contributions */
  static calculateFutureValueOfBalance(
    currentBalance: number | null | undefined,
    annualReturn: number,
    yearsToTarget: number
  ): CalculationResult<number> {
    if (!isValidNumber(currentBalance) || currentBalance < 0) return PensionProjectionEngine.skipped("INVALID_BALANCE");
    if (!isValidNumber(annualReturn) || annualReturn <= -1) return PensionProjectionEngine.skipped("INVALID_RETURN");
    return PensionProjectionEngine.calculated(FinancialCalculatorService.fv(annualReturn, yearsToTarget, 0, currentBalance));
  }

  /** netMonthlyContribution = monthlyContribution × (1 - contributionFeeRate) */
  static calculateNetMonthlyContribution(
    monthlyContribution: number | null | undefined,
    contributionFeeRate: number | null | undefined
  ): CalculationResult<number> {
    if (!isValidNumber(monthlyContribution) || monthlyContribution <= 0) {
      return PensionProjectionEngine.skipped("MISSING_CONTRIBUTION");
    }
    const feeRate = isValidNumber(contributionFeeRate) ? contributionFeeRate : 0;
    return PensionProjectionEngine.calculated(monthlyContribution * (1 - feeRate));
  }

  /**
   * FV of a series of equal monthly contributions made at the end of each month:
   * netMonthlyContribution × [((1 + monthlyRate)^n - 1) / monthlyRate], monthlyRate = (1 + annualReturn)^(1/12) - 1
   */
  static calculateFutureValueOfContributions(
    netMonthlyContribution: number | null | undefined,
    annualReturn: number,
    months: number
  ): CalculationResult<number> {
    if (!isValidNumber(netMonthlyContribution) || netMonthlyContribution <= 0) {
      return PensionProjectionEngine.skipped("MISSING_CONTRIBUTION");
    }
    if (!isValidNumber(annualReturn) || annualReturn <= -1) return PensionProjectionEngine.skipped("INVALID_RETURN");
    return PensionProjectionEngine.calculated(
      FinancialCalculatorService.fv(PensionProjectionEngine.toEffectiveMonthlyRate(annualReturn), months, netMonthlyContribution, 0)
    );
  }

  /** FV_with_contributions = FV_existing + FV_contributions — skipped when either part was skipped */
  static calculateFutureValueWithContributions(
    futureValueOfBalance: CalculationResult<number>,
    futureValueOfContributions: CalculationResult<number>
  ): CalculationResult<number> {
    if (!PensionProjectionEngine.isCalculated(futureValueOfBalance)) return futureValueOfBalance;
    if (!PensionProjectionEngine.isCalculated(futureValueOfContributions)) return futureValueOfContributions;
    return PensionProjectionEngine.calculated(futureValueOfBalance.value + futureValueOfContributions.value);
  }

  // ---------------------------------------------------------------------------
  // Pension
  // ---------------------------------------------------------------------------

  /** estimatedMonthlyPension = futureAnnuityCapital / coefficient — only with a valid coefficient */
  static calculateEstimatedMonthlyPension(
    futureAnnuityCapital: CalculationResult<number>,
    coefficient: number | null | undefined
  ): CalculationResult<number> {
    if (!PensionProjectionEngine.isCalculated(futureAnnuityCapital)) return futureAnnuityCapital;
    if (!isValidNumber(coefficient) || coefficient <= 0) return PensionProjectionEngine.skipped("MISSING_COEFFICIENT");
    return PensionProjectionEngine.calculated(futureAnnuityCapital.value / coefficient);
  }
}
